import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';

import MidPageBanner from 'Components/Banner/MidPageBanner/MidPageBanner';
import { fetchProduct } from 'Redux/ducks/product';
import GeneralUtils from 'Helpers/GeneralUtils';
import { countryCode } from 'Helpers/Geo/data';
import checkExtensionInstalled from 'Helpers/Extension/CheckExtensionInstalled';
import ExtensionMidPageBanner from 'Components/Banner/ExtensionMidPageBanner';
import useViewport from 'Hooks/useViewport';

const MidPageBannerContainer = ({
  adverts,
  appearsOn,
  siteBrand,
  userLibrary,
  steamLibrary,
}) => {
  const [product, setProduct] = useState(null);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const { isAboveMd } = useViewport();

  const midPageBanner = _get(adverts, 'MidPageBanner', []).find(ad => _get(ad, `appears_on[${appearsOn}]`));
  const url = _get(midPageBanner, 'url', '');
  const productSlug = url.split('/')[5];

  const showFallbackbBanner = () => {
    if (window.chrome && !extensionInstalled && isAboveMd) {
      return (
        <section className="container">
          <ExtensionMidPageBanner appearsOn={`${upperFirst(camelCase(appearsOn))}Page`} />
        </section>
      );
    }

    return false;
  };

  useEffect(() => {
    const installCheck = async () => {
      if (extensionInstalled) return;
      setTimeout(async () => {
        let isInstalled = false;

        try {
          await checkExtensionInstalled(true);
          isInstalled = true;
        } catch (e) {
          console.warn('Extension not installed'); // eslint-disable-line no-console
        }

        if (isInstalled) setExtensionInstalled(true);
      }, 500);
    };
    installCheck();
  }, [extensionInstalled]);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productSlug) return false;

      return fetchProduct(productSlug)
        .then((result) => {
          if (result.notFound) {
            return false;
          }
          return setProduct(result.product);
        });
    };

    loadProduct();
  }, [productSlug]);

  if (isEmpty(midPageBanner)) return showFallbackbBanner();

  const isSignInAndWinAdvert = midPageBanner._id === '650da63caa01fc3dca6aa2aa';

  if (isSignInAndWinAdvert && siteBrand !== 'fanatical') return showFallbackbBanner();

  const background = midPageBanner.images.find(img => img.img_type === 'mid_page_banner_background');
  const foreground = midPageBanner.images.find(img => img.img_type === 'mid_page_banner_foreground');
  const mobile = midPageBanner.images.find(img => img.img_type === 'mid_page_banner_mobile');
  const externalLink = _get(midPageBanner, 'external_link');

  const productTypes = ['game', 'bundle', 'dlc', 'book', 'comic', 'audio', 'video', 'software', 'gift-card'];
  const isProductBanner = (!externalLink && url && url.split && url.split('/')[4] && productTypes.includes(url.split('/')[4])) && productSlug;

  const newUser = GeneralUtils.newUser();
  const canShowUserGiveawayBanner = newUser;
  const soldOut = _get(product, 'sold_out', false);
  const giveaway = _get(product, 'giveaway', false);
  const includedCodes = _get(product, 'regions_included', []).map(country => country.code);
  const excludedCodes = _get(product, 'regions_excluded', []).map(country => country.code);

  // Combine fanatical owned and steam owned
  const purchaseHistoryNames = [...userLibrary, ...steamLibrary];

  let lockedInRegion = false;
  if (excludedCodes.includes(countryCode)) lockedInRegion = true;
  if (!isEmpty(includedCodes) && !includedCodes.includes(countryCode)) lockedInRegion = true;

  const canShowUserProductBanner = !isEmpty(product) && !soldOut && !lockedInRegion;
  const isGiveawayBanner = !isEmpty(product) && !soldOut && !lockedInRegion && giveaway;

  if (!isEmpty(product) && purchaseHistoryNames.includes(product.name)) {
    return showFallbackbBanner();
  }

  if (isProductBanner && !canShowUserProductBanner) {
    return showFallbackbBanner();
  }

  if (isGiveawayBanner && !canShowUserGiveawayBanner) {
    return showFallbackbBanner();
  }

  return (
    <MidPageBanner
      url={midPageBanner.url}
      background={_get(background, 'img')}
      foreground={_get(foreground, 'img')}
      mobile={_get(mobile, 'img')}
      externalLink={midPageBanner.external_link}
      needsContainer={appearsOn === 'home' || appearsOn === 'on_sale'}
    />
  );
};

MidPageBannerContainer.propTypes = {
  adverts: PropTypes.shape({}),
  appearsOn: PropTypes.oneOf([
    'home',
    'on_sale',
    'gift_guide',
    'cyber_monday',
    'black_friday',
    'fantasyverse',
  ]).isRequired,
  siteBrand: PropTypes.string,
  userLibrary: PropTypes.arrayOf(PropTypes.string),
  steamLibrary: PropTypes.arrayOf(PropTypes.string),
};

MidPageBannerContainer.defaultProps = {
  adverts: {},
  siteBrand: '',
  userLibrary: [],
  steamLibrary: [],
};

const mapStateToProps = ({
  adverts,
  router: { location },
  siteBrand,
  userLibrary,
  steamLibrary,
}) => ({
  adverts,
  pathname: location.pathname,
  siteBrand,
  userLibrary,
  steamLibrary,
});

export default connect(mapStateToProps)(MidPageBannerContainer);
